import { useLocation, useNavigate } from "react-router-dom";
import { Menu } from "lucide-react";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import Sidebar from "./Sidebar";
import { useBranding } from "@/context/BrandingContext";
import ThemeToggle from "@/components/ThemeToggle";
import SettingsNav, { isSettingsPath } from "@/components/SettingsNav";

export default function AppShell({ children }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { branding } = useBranding();

  const handleNavigate = (path) => {
    navigate(path);
    setMobileOpen(false);
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex bg-transparent">
      {/* Sidebar desktop */}
      <div className="hidden lg:block sticky top-0 h-screen shrink-0">
        <Sidebar
          currentPath={location.pathname}
          onNavigate={handleNavigate}
          onLogout={handleLogout}
          user={user}
        />
      </div>

      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent
          side="left"
          className="w-[240px] border-r border-[var(--fpccs-border)] bg-[var(--fpccs-sidebar)] p-0 backdrop-blur-xl"
        >
          <Sidebar
            currentPath={location.pathname}
            onNavigate={handleNavigate}
            onLogout={handleLogout}
            user={user}
            collapsible={false}
          />
        </SheetContent>
      </Sheet>

      <main className="flex-1 min-w-0">
        <header className="glass-header sticky top-0 z-30 flex h-16 items-center justify-between px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => setMobileOpen(true)}
            aria-label="Buka menu"
            className="glass-control flex h-10 w-10 items-center justify-center rounded-xl text-[var(--fpccs-text-muted)] lg:hidden"
          >
            <Menu className="w-4 h-4" />
          </button>
          <div className="hidden text-xs font-medium uppercase tracking-[0.18em] text-[var(--fpccs-text-subtle)] lg:block">{branding.application_name}</div>
          <ThemeToggle />
        </header>

        <div className="px-4 sm:px-6 lg:px-8 py-6 max-w-[1400px] mx-auto">
          {isSettingsPath(location.pathname) && <SettingsNav />}
          {children}
        </div>
        {(branding.footer_text || branding.copyright_text || branding.website || branding.contact_email) && <footer className="px-6 pb-5 text-center text-[11px] text-[var(--fpccs-text-subtle)]"><div>{branding.copyright_text || `© ${new Date().getFullYear()} ${branding.company_name}`}{branding.footer_text ? ` · ${branding.footer_text}` : ''}</div>{(branding.website || branding.contact_email || branding.contact_phone) && <div className="mt-1 flex flex-wrap justify-center gap-x-3 gap-y-1">{branding.website && <a className="hover:text-[var(--fpccs-primary)]" href={branding.website} target="_blank" rel="noreferrer">Website</a>}{branding.contact_email && <a className="hover:text-[var(--fpccs-primary)]" href={`mailto:${branding.contact_email}`}>{branding.contact_email}</a>}{branding.contact_phone && <a className="hover:text-[var(--fpccs-primary)]" href={`tel:${branding.contact_phone}`}>{branding.contact_phone}</a>}</div>}</footer>}
      </main>
    </div>
  );
}
